import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneCall, X } from "lucide-react";
import api from "../lib/api";
import { useTimezone } from "../context/TimezoneContext";
import { countryFlag } from "../lib/constants";

// Surfaces scheduled appointments as live toast pop-ups across the whole app
// (like the original CRM). Each reminder is shown once per browser session
// unless dismissed.
const DISMISS_KEY = "crm-dismissed-reminders";

function getDismissed() {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveDismissed(set) {
  sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
}

export default function ReminderToasts() {
  const nav = useNavigate();
  const { tz } = useTimezone();
  const [active, setActive] = useState([]);
  const dismissed = useRef(getDismissed());

  const fmtTime = useCallback((iso) => {
    try {
      return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(iso));
    } catch { return ""; }
  }, [tz]);

  const relative = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const abs = Math.abs(diff);
    const m = Math.round(abs / 60000), h = Math.round(abs / 3600000), d = Math.round(abs / 86400000);
    const unit = d >= 1 ? `${d} day${d > 1 ? "s" : ""}` : h >= 1 ? `${h} hour${h > 1 ? "s" : ""}` : `${m} min`;
    return diff >= 0 ? `${unit} ago` : `in ${unit}`;
  };

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/reminders");
      const items = data.items.filter((r) => r.callback_at);
      const now = Date.now();
      const upcoming = items.filter((r) => new Date(r.callback_at).getTime() >= now);
      const overdue = items.filter((r) => new Date(r.callback_at).getTime() < now)
        .sort((a, b) => new Date(b.callback_at) - new Date(a.callback_at));
      // prefer soonest upcoming, otherwise the most-recently-due appointments
      const pool = (upcoming.length ? upcoming : overdue).slice(0, 4);
      const fresh = pool.filter((r) => !dismissed.current.has(r.id));
      if (fresh.length) {
        setActive((prev) => {
          const ids = new Set(prev.map((p) => p.id));
          return [...prev, ...fresh.filter((f) => !ids.has(f.id))].slice(-4);
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [poll]);

  const dismiss = (id) => {
    dismissed.current.add(id);
    saveDismissed(dismissed.current);
    setActive((prev) => prev.filter((p) => p.id !== id));
  };

  if (!active.length) return null;

  return (
    <div className="fixed top-20 right-4 z-[150] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]" data-testid="reminder-toasts">
      {active.map((r) => (
        <div
          key={r.id}
          onClick={() => { nav(`/${r.client_type}s/${r.id}`); dismiss(r.id); }}
          data-testid={`reminder-toast-${r.id}`}
          className="group cursor-pointer rounded-xl border border-rose-200 dark:border-rose-900/60 bg-white dark:bg-[#111827] shadow-lg p-3.5 animate-slide-up"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 shrink-0 rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-500 flex items-center justify-center animate-pulse">
              <PhoneCall className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">Appointment Reminder</span>
                <button onClick={(e) => { e.stopPropagation(); dismiss(r.id); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" data-testid={`reminder-dismiss-${r.id}`} aria-label="Dismiss">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">
                {countryFlag(r.country)} Client <b>"{r.name}"</b> has a scheduled appointment at {fmtTime(r.callback_at)}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">{relative(r.callback_at)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
